import {
  ActionIcon,
  Flex,
  Indicator,
  Popover,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconFileSpreadsheet, IconFilter, IconPlus } from "@tabler/icons-react";
import { partnerApi } from "apis/partnerApi";
import GroupButton, { ActionButton } from "components/ui/GroupButton/GroupButton";
import { SearchInput } from "components/ui/SearchInput/SearchInput";
import CustomText from "components/ui/CustomText/CustomText";
import { PARTNER_TYPE_OPTIONS } from "constants/common/partner";
import { CommonParamsType } from "constants/types/common";
import { PartnerParamsType, PartnerType } from "constants/types/partner";
import { FORM_IDS } from "pages/Partner/config";
import { FC, useMemo, useRef, useState } from "react";
import { usePopoverClickOutside } from "utils/hooks/ui/usePopoverClickOutside";
import { useSWRConfig } from "swr";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import { useNotification } from "utils/hooks";
import { getPartnerListKey } from "utils/hooks/request/usePartners";
import CreateForm from "../CreateForm/CreateForm";

type PartnerActionsProps = {
  handleSearch: (v: string) => void;
  params: PartnerParamsType;
  onFilterChange: (filters: Partial<Omit<PartnerParamsType, keyof CommonParamsType>>) => void;
};

const PartnerActions: FC<PartnerActionsProps> = ({ handleSearch, params, onFilterChange }) => {
  const { openCrudModal, closeCrudModal } = useAppModal();
  const activeModalId = useRef<string | null>(null);
  const { mutate } = useSWRConfig();
  const { notification } = useNotification();
  const [filterOpened, { close: closeFilter, toggle: toggleFilter }] = useDisclosure(false);
  const { triggerRef, dropdownRef } = usePopoverClickOutside(filterOpened, closeFilter);
  const [isExporting, setIsExporting] = useState(false);

  const activeFilterCount = useMemo(
    () => [params.type].filter(Boolean).length,
    [params.type],
  );

  const handleCloseModal = () => {
    if (activeModalId.current) {
      closeCrudModal(activeModalId.current);
      activeModalId.current = null;
    }
  };

  const handleOpenCreateModal = () => {
    const modalId = openCrudModal(
      <CustomText variant="section-title">Tạo mới đối tác</CustomText>,
      {
        mode: "create",
        formId: FORM_IDS.CREATE,
        children: (
          <CreateForm
            mutate={() => mutate(getPartnerListKey(params))}
            onClose={handleCloseModal}
          />
        ),
        submitText: "Tạo mới",
      },
      750,
    );
    activeModalId.current = modalId;
  };

  const handleExportExcel = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const exportParams = params.type ? { type: params.type } : undefined;
      const res = await partnerApi.export(exportParams);
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "doi-tac.xlsx";
      link.click();
      URL.revokeObjectURL(url);
      notification.success({ title: "Xuất Excel thành công" });
    } catch {
      notification.error({ title: "Xuất Excel thất bại" });
    } finally {
      setIsExporting(false);
    }
  };

  const actions: ActionButton[] = [
    {
      key: "export-excel",
      label: "Xuất Excel",
      icon: <IconFileSpreadsheet size={18} />,
      variant: "filled",
      color: "#217346",
      isLoading: isExporting,
      disabled: isExporting,
      onClick: handleExportExcel,
      permission: "partner:export",
    },
    {
      key: "create",
      label: "Tạo mới",
      icon: <IconPlus size={18} />,
      variant: "filled",
      color: "blue",
      isLoading: false,
      onClick: handleOpenCreateModal,
      permission: "partner:create",
    },
  ];

  return (
    <Flex justify="space-between" align="center">
      <Flex align="center" gap="sm">
        <SearchInput
          value={params.search || ""}
          onChange={handleSearch}
          placeholder="Tìm kiếm thông tin đối tác"
        />
        <Popover
          opened={filterOpened}
          onClose={closeFilter}
          position="bottom-start"
          shadow="md"
          withinPortal
          closeOnClickOutside={false}
        >
          <Popover.Target>
            <Indicator
              label={activeFilterCount}
              size={16}
              disabled={activeFilterCount === 0}
              color="blue"
            >
              <ActionIcon
                ref={triggerRef}
                variant="default"
                size="lg"
                onClick={toggleFilter}
                aria-label="Bộ lọc"
              >
                <IconFilter size={18} />
              </ActionIcon>
            </Indicator>
          </Popover.Target>

          <Popover.Dropdown>
            <div ref={dropdownRef}>
            <Stack gap="sm" w={280}>
              <Text size="sm" fw={600}>
                Bộ lọc
              </Text>
              <Select
                label="Loại đối tác"
                placeholder="Tất cả"
                data={PARTNER_TYPE_OPTIONS}
                value={params.type ?? null}
                onChange={(v) => onFilterChange({ type: (v as PartnerType) ?? undefined })}
                clearable
                size="sm"
              />
            </Stack>
            </div>
          </Popover.Dropdown>
        </Popover>
      </Flex>
      <GroupButton actions={actions} />
    </Flex>
  );
};

export default PartnerActions;
